import React, { useState } from 'react';
import styled from '@emotion/styled';
import Modal from 'react-modal';
import PageHeader from '../components/PageHeader';

Modal.setAppElement('#root');

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 2rem;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    background: 'rgba(0, 0, 0, 0.95)',
    border: '2px solid rgba(255, 51, 51, 0.3)',
    borderRadius: '15px',
    padding: '1rem',
    width: 'min(1100px, 95vw)',
    maxHeight: '90vh',
    overflow: 'auto',
    backdropFilter: 'blur(10px)',
    zIndex: 1000,
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    backdropFilter: 'blur(5px)',
    zIndex: 999,
  },
};

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const ModalTitle = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.75rem;
  line-height: 1;
  cursor: pointer;
  padding: 0.25rem 0.5rem;

  &:hover {
    color: white;
  }
`;

const ModalImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 12px;
  border: 1px solid rgba(255, 51, 51, 0.25);
`;

const ContentContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  position: relative;
  z-index: 2;
`;

const Panel = styled.div`
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 15px;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const Intro = styled(Panel)`
  text-align: center;

  p {
    font-size: 1.1rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
    max-width: 900px;
    margin: 0.75rem auto 0;
  }
`;

const ServerUrl = styled.div`
  margin: 1rem auto 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  background: rgba(255, 51, 51, 0.12);
  border: 1px solid rgba(255, 51, 51, 0.35);
  color: rgba(255, 255, 255, 0.95);
  width: fit-content;
  max-width: 100%;

  code {
    background: rgba(0, 0, 0, 0.35);
    padding: 0.25rem 0.5rem;
    border-radius: 8px;
    color: #ffb3b3;
    word-break: break-all;
    text-align: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
  }
`;

const JumpLinks = styled.div`
  margin-top: 1.5rem;
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const JumpLink = styled.a`
  text-decoration: none;
  color: white;
  border: 1px solid rgba(255, 51, 51, 0.35);
  background: rgba(0, 0, 0, 0.35);
  padding: 0.6rem 0.9rem;
  border-radius: 999px;
  font-size: 0.95rem;
  transition: transform 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(255, 51, 51, 0.65);
  }
`;

const Section = styled(Panel)`
  scroll-margin-top: 90px;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.25rem;

  h2 {
    margin: 0;
    font-size: 1.6rem;
    color: #ff3333;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.6;
  }
`;

const SubTitle = styled.h3`
  margin: 1.5rem 0 0.75rem 0;
  font-size: 1.15rem;
  color: rgba(255, 255, 255, 0.95);
`;

const Steps = styled.ol`
  margin: 0;
  padding-left: 1.2rem;
  color: rgba(255, 255, 255, 0.9);

  li {
    margin: 0.5rem 0;
    line-height: 1.6;
  }

  code {
    background: rgba(0, 0, 0, 0.35);
    padding: 0.15rem 0.35rem;
    border-radius: 6px;
    color: #ffb3b3;
  }
`;

const ImageGrid = styled.div`
  margin-top: 1rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ImageCard = styled.div`
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 51, 51, 0.25);
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(255, 51, 51, 0.55);
  }
`;

const Screenshot = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const Caption = styled.div`
  padding: 0.75rem 1rem;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.95rem;
  line-height: 1.4;
`;

const CloudHighlight = styled.span`
  color: #ff3333;
`;

const LinksRow = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const ExternalLink = styled.a`
  color: #ff3333;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    opacity: 0.85;
  }
`;

const Disclaimer = styled(Panel)`
  text-align: center;

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.6;
    font-size: 0.95rem;
  }

  a {
    color: #ff3333;
    text-decoration: none;
  }
`;

function HowToUse() {
  const serverUrl = 'https://my.redcloudstorage.net';
  const [lightbox, setLightbox] = useState(null);

  const renderCloudText = (text) => {
    if (typeof text !== 'string' || text.length === 0) return text;

    const parts = text.split(/(Cloud)/g);
    return parts.map((part, idx) => (
      part === 'Cloud'
        ? <CloudHighlight key={idx}>{part}</CloudHighlight>
        : <React.Fragment key={idx}>{part}</React.Fragment>
    ));
  };

  const renderCloudChildren = (node) => {
    if (node === null || node === undefined || typeof node === 'boolean') return node;
    if (typeof node === 'string') return renderCloudText(node);
    if (typeof node === 'number') return node;

    if (Array.isArray(node)) {
      return node.map((child, idx) => (
        <React.Fragment key={idx}>{renderCloudChildren(child)}</React.Fragment>
      ));
    }

    if (React.isValidElement(node)) {
      if (node.type === 'code') {
        return node;
      }

      const children = renderCloudChildren(node.props.children);
      return React.cloneElement(node, { ...node.props, children });
    }

    return node;
  };

  const CloudText = ({ children }) => (
    <>{renderCloudChildren(children)}</>
  );

  const windowsScreens = [
    {
      src: require('../images/howto/desktop/windows/desktop-03-open-client.png'),
      alt: 'Open the desktop client',
      caption: 'Open the desktop app and click “Log in” to connect your account.'
    },
    {
      src: require('../images/howto/desktop/windows/desktop-04-enter-server-url.png'),
      alt: 'Enter server URL',
      caption: 'Enter the server address to connect the app to RedCloud Storage.'
    },
    {
      src: require('../images/howto/desktop/windows/desktop-07-choose-sync-folders.png'),
      alt: 'Choose sync folders',
      caption: 'Choose which folders to sync (all files or selected folders).'
    },
    {
      src: require('../images/howto/desktop/windows/windows-settings-sync-large-folder-confirmation.png'),
      alt: 'Large folder confirmation setting',
      caption: 'Optional: disable or increase the “confirm before syncing large folders” threshold for big sync jobs.'
    }
  ];

  const macosScreens = [
    {
      src: require('../images/howto/desktop/macos/macos-03-open-nextcloud-client.png'),
      alt: 'Open the macOS client',
      caption: 'Open the desktop app and click “Log in” to connect your account.'
    },
    {
      src: require('../images/howto/desktop/macos/macos-04-enter-server-url-my-redcloudstorage.png'),
      alt: 'Enter server URL on macOS',
      caption: 'Enter the server address to connect: my.redcloudstorage.net.'
    },
    {
      src: require('../images/howto/desktop/macos/macos-07-choose-local-folder.png'),
      alt: 'Choose local folder on macOS',
      caption: 'Choose where your RedCloud Storage files will appear on your Mac.'
    },
    {
      src: require('../images/howto/desktop/macos/macos-settings-sync-large-folder-confirmation.png'),
      alt: 'Large folder confirmation setting on macOS',
      caption: 'Optional: disable or increase the “confirm before syncing large folders” threshold.'
    }
  ];

  const macosVfsScreens = [
    {
      src: require('../images/howto/desktop/macos-vfs/macos-vfs-01-finder-locations-domain.png'),
      alt: 'Virtual files location in Finder',
      caption: 'Virtual Files appear under “Locations” in Finder.'
    },
    {
      src: require('../images/howto/desktop/macos-vfs/macos-vfs-03-status-icons-cloud.png'),
      alt: 'Virtual file status icons',
      caption: 'Finder icons show whether a file is online-only or available offline.'
    },
    {
      src: require('../images/howto/desktop/macos-vfs/macos-vfs-04-right-click-keep-offline.png'),
      alt: 'Keep offline option',
      caption: 'Use “Keep Offline” to ensure files stay downloaded.'
    },
    {
      src: require('../images/howto/desktop/macos-vfs/macos-vfs-05-right-click-remove-download.png'),
      alt: 'Remove download option',
      caption: 'Use “Remove Download” to free up space while keeping files available on demand.'
    }
  ];

  const iosScreens = [
    {
      src: require('../images/howto/mobile/ios/ios-setup-03-enter-server-url-my-redcloudstorage.png'),
      alt: 'iOS enter server URL',
      caption: 'Add your RedCloud Storage server address on iPhone.'
    },
    {
      src: require('../images/howto/mobile/ios/ios-setup-06-account-added.png'),
      alt: 'iOS account added',
      caption: 'After signing in, your account is connected.'
    },
    {
      src: require('../images/howto/mobile/ios/ios-upload-01-open-plus-menu.png'),
      alt: 'iOS upload menu',
      caption: 'Use the plus button to upload files or photos.'
    },
    {
      src: require('../images/howto/mobile/ios/ios-auto-upload-03-enable-toggle.png'),
      alt: 'Enable auto upload',
      caption: 'Optional: enable Auto Upload/Camera Upload so new photos upload automatically.'
    }
  ];

  const androidScreens = [
    {
      src: require('../images/howto/mobile/android/android-setup-02-open-app.png'),
      alt: 'Android open app',
      caption: 'Install and open the Nextcloud Android app.'
    },
    {
      src: require('../images/howto/mobile/android/android-setup-04-login.png'),
      alt: 'Android login',
      caption: 'Sign in to your RedCloud Storage account.'
    },
    {
      src: require('../images/howto/mobile/android/android-auto-upload-01-open-plus-menu.png'),
      alt: 'Android upload menu',
      caption: 'Manual upload: open the upload menu to send files from your phone to RedCloud Storage.'
    },
    {
      src: require('../images/howto/mobile/android/android-2way-03-tap-two-way-sync.png'),
      alt: 'Android two-way sync',
      caption: 'Two-way sync: set a local folder to stay in sync both ways.'
    },
    {
      src: require('../images/howto/mobile/android/android-auto-upload-07-wifi-only-setting.png'),
      alt: 'Wi‑Fi only setting',
      caption: 'Optional: configure Wi‑Fi-only and other upload preferences.'
    }
  ];

  const renderScreens = (screens) => (
    <ImageGrid>
      {screens.map((s, idx) => (
        <ImageCard
          key={idx}
          onClick={() => setLightbox(s)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setLightbox(s);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={s.alt || 'Open screenshot'}
        >
          <Caption>{renderCloudText(s.caption)}</Caption>
          <Screenshot src={s.src} alt={s.alt} loading="lazy" />
        </ImageCard>
      ))}
    </ImageGrid>
  );

  return (
    <PageContainer>
      <ContentContainer>
        <PageHeader title="How To Use RedCloud Storage" highlightWords={["To", "Cloud"]} />

        <CloudText>
          <Intro>
            <p>
              RedCloud Storage uses the official Nextcloud desktop and mobile apps to connect you to your account and keep your files synchronized across devices.
            </p>
            <ServerUrl>
              Server URL
              <code>{serverUrl}</code>
            </ServerUrl>
            <JumpLinks>
              <JumpLink href="#desktop-windows">Desktop (Windows)</JumpLink>
              <JumpLink href="#desktop-macos">Desktop (macOS)</JumpLink>
              <JumpLink href="#desktop-macos-vfs">macOS Virtual Files</JumpLink>
              <JumpLink href="#mobile-ios">iPhone (iOS)</JumpLink>
              <JumpLink href="#mobile-android">Android</JumpLink>
            </JumpLinks>
          </Intro>

          <Section id="desktop-windows">
            <SectionHeader>
              <h2>Desktop (Windows)</h2>
              <p>
                Install the desktop sync client, connect it to your RedCloud Storage account, then choose what folders you want on your PC.
              </p>
            </SectionHeader>

          <SubTitle>Connect your account</SubTitle>
          <Steps>
            <li>Download and install the desktop app from our <code>Download</code> page.</li>
            <li>Open the app and click <code>Log in</code>.</li>
            <li>Enter the server address: <code>{serverUrl}</code>.</li>
            <li>Your browser opens for sign-in. Log in and approve access.</li>
            <li>Pick a local folder and use <code>Choose what to sync</code> (Selective Sync) to select only the folders you need (recommended).</li>
          </Steps>

          <SubTitle>Recommended setting for large folders (optional)</SubTitle>
          <Steps>
            <li>If you regularly sync very large folders (for example 60GB+), disable the “confirm before syncing large folders” prompt, or increase the threshold.</li>
            <li>This helps avoid repeated confirmation prompts when syncing big project folders.</li>
          </Steps>

          {renderScreens(windowsScreens)}

          <LinksRow>
            <ExternalLink href="/download">Download page →</ExternalLink>
            <ExternalLink href="https://docs.nextcloud.com/server/latest/user_manual/en/desktop/installation.html" target="_blank" rel="noopener noreferrer">
              Official desktop install docs →
            </ExternalLink>
          </LinksRow>
        </Section>

        <Section id="desktop-macos">
          <SectionHeader>
            <h2>Desktop (macOS)</h2>
            <p>
              Install the macOS desktop app, connect your account using your server URL, then choose which folders you want synced locally.
            </p>
          </SectionHeader>

          <SubTitle>Connect your account</SubTitle>
          <Steps>
            <li>Download and install the macOS app from our <code>Download</code> page.</li>
            <li>Open the app and click <code>Log in</code>.</li>
            <li>Enter <code>{serverUrl}</code>.</li>
            <li>Complete sign-in in your browser and approve access.</li>
            <li>Choose your local folder and use Selective Sync to avoid syncing everything unless you need it.</li>
          </Steps>

          <SubTitle>Recommended setting for large folders (optional)</SubTitle>
          <Steps>
            <li>If you routinely sync large folders, disable or increase the confirmation prompt for new large folders.</li>
          </Steps>

          {renderScreens(macosScreens)}

          <LinksRow>
            <ExternalLink href="/download">Download page →</ExternalLink>
            <ExternalLink href="https://docs.nextcloud.com/server/latest/user_manual/en/desktop/usage.html" target="_blank" rel="noopener noreferrer">
              Official desktop usage docs →
            </ExternalLink>
          </LinksRow>
        </Section>

        <Section id="desktop-macos-vfs">
          <SectionHeader>
            <h2>macOS Virtual Files (Optional)</h2>
            <p>
              Virtual Files lets you browse your RedCloud Storage files in Finder without downloading everything. Files download when you open them, and you can keep important items offline.
            </p>
          </SectionHeader>

          <SubTitle>How Virtual Files works</SubTitle>
          <Steps>
            <li>Your files appear under Finder “Locations”.</li>
            <li>Use Finder status icons to see what’s available offline vs on-demand.</li>
            <li>Right-click files/folders to keep them offline or remove downloads to save space.</li>
          </Steps>

          {renderScreens(macosVfsScreens)}

          <LinksRow>
            <ExternalLink href="https://docs.nextcloud.com/server/latest/user_manual/en/desktop/macosvfs.html" target="_blank" rel="noopener noreferrer">
              Official macOS Virtual Files docs →
            </ExternalLink>
          </LinksRow>
        </Section>

        <Section id="mobile-ios">
          <SectionHeader>
            <h2>iPhone (iOS)</h2>
            <p>
              Connect the iOS app to your RedCloud Storage account, then upload files/photos and optionally enable automatic uploads.
            </p>
          </SectionHeader>

          <SubTitle>Connect your account</SubTitle>
          <Steps>
            <li>Install the Nextcloud app from the App Store.</li>
            <li>Add an account using <code>{serverUrl}</code>, then sign in and approve access.</li>
          </Steps>

          <SubTitle>Upload and Auto Upload</SubTitle>
          <Steps>
            <li>Use the plus button to upload files or photos into your chosen folder.</li>
            <li>Optional: enable Auto Upload/Camera Upload so new photos upload automatically.</li>
          </Steps>

          {renderScreens(iosScreens)}

          <LinksRow>
            <ExternalLink href="/download">Download page →</ExternalLink>
          </LinksRow>
        </Section>

        <Section id="mobile-android">
          <SectionHeader>
            <h2>Android</h2>
            <p>
              Use the Android app to upload files, set auto-upload preferences, and optionally configure two-way sync for folders that should stay mirrored.
            </p>
          </SectionHeader>

          <SubTitle>Connect your account</SubTitle>
          <Steps>
            <li>Install the Nextcloud app from Google Play.</li>
            <li>Sign in with your RedCloud Storage account and approve access if prompted.</li>
          </Steps>

          <SubTitle>Manual upload</SubTitle>
          <Steps>
            <li>Open the upload menu to select files/folders to upload to RedCloud Storage.</li>
          </Steps>

          <SubTitle>Two-way sync (optional)</SubTitle>
          <Steps>
            <li>Use the two-way sync feature to keep a chosen local folder synchronized in both directions.</li>
          </Steps>

          <SubTitle>Auto-upload preferences (optional)</SubTitle>
          <Steps>
            <li>Configure Wi‑Fi only and other upload preferences to match your connection and data plan.</li>
          </Steps>

          {renderScreens(androidScreens)}

          <LinksRow>
            <ExternalLink href="/download">Download page →</ExternalLink>
          </LinksRow>
        </Section>

          <Disclaimer>
            <p>
              RedCloud Storage is powered by Nextcloud. Nextcloud and the Nextcloud logo are trademarks of Nextcloud GmbH. For additional details, see the official Nextcloud documentation:
              {' '}
              <a href="https://docs.nextcloud.com/server/latest/user_manual/en/desktop/index.html" target="_blank" rel="noopener noreferrer">Desktop User Manual</a>.
            </p>
          </Disclaimer>
        </CloudText>

        <Modal
          isOpen={!!lightbox}
          onRequestClose={() => setLightbox(null)}
          style={modalStyles}
          contentLabel="Screenshot preview"
          shouldCloseOnOverlayClick
        >
          <ModalHeader>
            <ModalTitle>{lightbox?.caption ? renderCloudText(lightbox.caption) : null}</ModalTitle>
            <CloseButton onClick={() => setLightbox(null)} aria-label="Close preview">&times;</CloseButton>
          </ModalHeader>
          {lightbox?.src ? (
            <ModalImage src={lightbox.src} alt={lightbox.alt || 'Screenshot'} />
          ) : null}
        </Modal>
      </ContentContainer>
    </PageContainer>
  );
}

export default HowToUse;
